<template>
  <div>
    <b-row class="mt-1">
      <b-col cols="6">
        <b-form-group
          label-for="parcel-tracking-number-tree"
        >
          <b-form-tags
            id="parcel-tracking-number-tree"
            v-model="filterDeclarationsByTrackingNumber"
            label="Parcel tracking numbers"
            :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
            separator=" ,;"
            placeholder="Enter tracking numbers separated by space, comma or semicolon"
            no-add-on-enter
            add-on-change
            clearable
            @input="applyFilters"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <div class="d-flex justify-content-between">
          <b-form-group
            class="full-width"
            label-for="container-codes"
          >
            <b-form-tags
              id="container-codes"
              v-model="filterContainerCodes"
              label="Container codes"
              :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
              separator=" ,;"
              placeholder="Enter container codes separated by space, comma or semicolon"
              no-add-on-enter
              add-on-change
              clearable
              @input="applyFilters"
            />
          </b-form-group>
          <div>
            <b-button
              v-if="filtersApplied"
              id="filter-reset"
              variant="outline-secondary"
              class="ml-2"
              @click="resetFilters"
            >
              Reset
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group
          label-for="mrn-tree"
        >
          <b-form-tags
            id="mrn-tree"
            v-model="filterDeclarationsByMrn"
            label="MRN codes"
            :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
            separator=" ,;"
            placeholder="Enter MRN codes separated by space, comma or semicolon"
            no-add-on-enter
            add-on-change
            clearable
            @input="applyFilters"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label-for="lrn-tree"
        >
          <b-form-tags
            id="lrn-tree"
            v-model="filterDeclarationsByLrn"
            label="LRN codes"
            :input-attrs="{ 'aria-describedby': 'tags-remove-on-delete-help' }"
            separator=" ,;"
            placeholder="Enter LRN codes separated by space, comma or semicolon"
            no-add-on-enter
            add-on-change
            clearable
            @input="applyFilters"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-table
      :items="parcels"
      responsive
      :fields="fields"
      :sort-by.sync="sortBy"
      :sort-desc.sync="isSortDirDesc"
      no-local-sorting
      show-empty
      :busy="dataLoading"
      empty-text="No matching records found"
      class="mb-0 parcels-tree-table"
    >
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle mr-1" />
          <strong>{{ dataLoadingMessage }}...</strong>
        </div>
      </template>

      <template #cell(tracking_number)="data">
        <div
          v-if="data.item.loading"
          class="text-center my-2"
        >
          <b-spinner class="align-middle mr-1" />
          <strong>Loading...</strong>
        </div>
        <div
          v-if="data.item.tracking_number"
          style="white-space: nowrap"
        >
          {{ data.item.tracking_number }}
        </div>
      </template>

      <template #cell(ens_status)="data">
        <div
          v-if="data.item.loading"
          class="text-center my-2"
        >
          <b-spinner class="align-middle mr-1" />
          <strong>Loading...</strong>
        </div>
        <div
          v-if="data.item.ens_status === 'ens_do_not_load'"
        >
          Message IE3Q01 (Do not load) is received.
          Notify airline asap that the parcel may not be loaded onto the plane!
        </div>
        <div
          v-if="data.item.ens_status === 'ens_additional_info_request'"
        >
          Message IE3Q02 (Additional information request) is received.
          Customs is asking for additional information. Please create ticket for x7trade team to identify details and help with the matter.“
        </div>
        <div
          v-if="data.item.ens_status === 'ens_hrcm_request'"
        >
          Message IE3Q03 (HRCM request) is received.
          Customs is asking for additional High Risk Cargo & Mail Screening for the parcel. Please create ticket for x7trade team to identify details and help with the matter.
        </div>
      </template>

      <template #cell(lrn)="data">
        {{ data.item.low_value_declaration.lrn }}
      </template>

      <template #cell(mrn)="data">
        {{ data.item.low_value_declaration.mrn }}
      </template>

      <template #cell(descriptions)="data">
        <div>
          {{
            data.item.items.map(function (parcelItem) {
              return parcelItem.description
            }).join(', ')
          }}
        </div>
      </template>

      <template #cell(hs_codes)="data">
        <div>
          {{
            data.item.items.map(function (parcelItem) {
              return parcelItem.hs_code
            }).join(', ')
          }}
        </div>
      </template>

      <template #cell(value)="data">
        <div
          v-if="typeof data.item.tracking_number !== 'undefined'"
          class="text-right"
        >
          {{
            data.item.items.reduce(function (sum, parcelItem) {
              return sum + parseFloat(parcelItem.value)
            }, 0) | formatNumber(2)
          }}
        </div>
      </template>

      <template #cell(actions)="data">
        <div v-if="data.item.tracking_number">
          <b-button-group>
            <view-parcel-button
              :declaration="data.item.low_value_declaration"
              :parcel="data.item"
            />
          </b-button-group>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import {
  BButton,
  BButtonGroup,
  BCol,
  BFormGroup,
  BFormTags,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'

import ViewParcelButton from '@/views/shipments/declarations/components/ViewParcelButton.vue'

export default {
  components: {
    BButtonGroup,
    BCol,
    BRow,
    BFormGroup,
    BTable,
    BSpinner,
    BFormTags,
    BButton,
    ViewParcelButton,
  },
  props: {
    dataLoading: {
      type: Boolean,
      default: false,
    },
    dataLoadingMessage: {
      type: String,
      default: 'Loading',
    },
    shipment: {
      type: Object,
      default: () => {
      },
    },
    filters: {
      type: Object,
      default: () => {
      },
    },
    parcels: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      sortBy: 'lrn',
      isSortDirDesc: false,
      filterContainerCodes: [],
      filterDeclarationsByTrackingNumber: [],
      filterDeclarationsByMrn: [],
      filterDeclarationsByLrn: [],
    }
  },
  computed: {
    localFilters: {
      get() {
        return this.filters
      },
      set(newFilters) {
        this.$emit('update:filters', newFilters)
      },
    },
    filtersApplied() {
      return this.filterContainerCodes.length > 0
        || this.filterDeclarationsByTrackingNumber.length > 0
        || this.filterDeclarationsByMrn.length > 0
        || this.filterDeclarationsByLrn.length > 0
    },
    fields() {
      return [
        {
          key: 'ens_status',
          label: 'Special Case Description',
        },
        {
          key: 'tracking_number',
          label: 'Tracking code',
        },
        {
          key: 'container_code',
          label: 'Container',
        },
        {
          key: 'descriptions',
          label: 'Description',
        },
        {
          key: 'hs_codes',
          label: 'HS code',
        },
        {
          key: 'lrn',
          label: 'LRN',
        },
        {
          key: 'mrn',
          label: 'MRN',
        },
        {
          key: 'actions',
          label: 'Actions',
        },
      ]
    },
  },
  methods: {
    resetFilters() {
      this.filterContainerCodes = []
      this.filterDeclarationsByTrackingNumber = []
      this.filterDeclarationsByMrn = []
      this.filterDeclarationsByLrn = []
      this.applyFilters()
    },
    applyFilters() {
      this.localFilters = {
        container_code: this.filterContainerCodes,
        tracking_number: this.filterDeclarationsByTrackingNumber,
        mrn: this.filterDeclarationsByMrn,
        lrn: this.filterDeclarationsByLrn,
      }
    },
  },
}
</script>
<style>
#filter-reset {
  padding-top: .63rem;
  padding-bottom: .63rem;
}
</style>
